/**
 * Datenzugriff des Kapitels „Hebel" (P4) der Sektion „Anfos & Umsatz". Backend: `node-app/src/routes/auswertungen.ts`
 * Block „P4 Hebel" → `services/auswertungen/hebelService.ts`. Typen spiegeln die Antworten dort.
 */
import { fetchData } from './apiUtils';
import type { KanalFensterMonate } from './auswertungenData';

async function unwrap<T>(url: string): Promise<T> {
  const res = await fetchData<T>(url);
  if (!res.success || res.data === undefined) throw new Error(res.error || 'Laden fehlgeschlagen');
  return res.data;
}

export interface HebelGrauSchwellen {
  anfos: number;
  anmeldungen: number;
  rechtzeitig: number;
  abmeldungen: number;
}

// ─── Wellen ───────────────────────────────────────────────────────────────────────────────────

export type WelleKey = 'vortag' | 'frueh' | 'spaet';

export interface WellenZahlen {
  versaende: number;
  kontakte: number;
  versaende_ganze_liste: number;
  anmeldungen: number;
  teilnehmer: number | null;
  rechtzeitig: number;
  teilnehmer_rechtzeitig: number;
  anfos: number;
  anfos_vorlaeufig: number;
  abmeldungen: number;
  /** Erwarteter Wert (netto) der Anfos ohne vorläufige, bewertet wie P3 „Webinar-Einladungen" (Persona-Umbau A2). */
  wert: number;
  anfos_ohne_wert: number;
}

export interface WellenQuoten {
  anmeldungen_je_1000: number | null;
  teilnehmer_je_1000: number | null;
  anfos_je_1000: number | null;
  abmeldungen_je_1000: number | null;
  wert_je_1000: number | null;
  abmeldungen_je_anmeldung: number | null;
}

export interface Versandzeit {
  zeit: string;
  clients: string[];
  teilmenge: boolean;
  ohne: string[];
  ganze_liste: boolean;
  nach_beginn: boolean;
}

export interface WelleZeile extends WellenZahlen, WellenQuoten {
  welle: WelleKey;
  label: string;
  fehlt: boolean;
  zeiten: Versandzeit[];
}

export interface WellenWebinar {
  webinar_id: string;
  public_title: string | null;
  concept: string | null;
  start_date: string;
  kommend: boolean;
  wellen: WelleZeile[];
  fehlende_wellen: number;
  summe: WellenZahlen & WellenQuoten;
  ausserhalb: { redlink: WellenZahlen | null; nicht_oeffner: WellenZahlen | null; nach_webinartag: WellenZahlen | null };
  /** Alle Anfos des Webinars inkl. vorläufiger — dieselbe Zahl wie die Anfo-Kachel der Detailseite. */
  anfos_webinar: number;
  teilnahme_grund: 'messluecke' | 'tracking_warnung' | null;
}

export interface WellenVergleich extends WellenZahlen, WellenQuoten {
  welle: WelleKey;
  label: string;
  kontakte_teilnahme: number;
}

export interface WellenAntwort {
  as_of: string;
  von: string;
  wochen: number;
  schnitt_stunde: number;
  soll: number;
  versandzeiten: { tag: number; stunde: number; versaende: number; webinare: number }[];
  schnitt_luecke: { frueh_bis: number | null; spaet_ab: number | null };
  webinare: WellenWebinar[];
  vergleich: { webinare: number; ohne_teilnahme: number; zeilen: WellenVergleich[] };
  grau_schwellen: HebelGrauSchwellen;
  /** Workshop-Lücke N2: Publisher-Liste × Welle (optional bis zum Deploy des Backends). */
  matrix?: WellenMatrix;
}

// ─── Matrix Liste × Welle (Workshop-Lücke N2) ─────────────────────────────────────────────────

export interface MatrixZelle extends WellenZahlen, WellenQuoten {
  welle: WelleKey;
  /** Webinare der Matrix, in denen die Liste in dieser Welle verschickt hat. */
  webinare: number;
  /** Anmeldungen je 1.000 ÷ Zeilenschnitt derselben Liste × 100; nur innerhalb der Zeile vergleichbar. */
  index: number | null;
}

export interface MatrixZeile {
  liste: string;
  label: string;
  clients: string[];
  /** `null` = in dieser Welle nie verschickt (leer, nicht 0). */
  zellen: Record<WelleKey, MatrixZelle | null>;
  summe: WellenZahlen & WellenQuoten;
  webinare: number;
}

export interface WellenMatrix {
  webinare: { webinar_id: string; public_title: string | null; start_date: string }[];
  soll_webinare: number;
  zeilen: MatrixZeile[];
  summe: Record<WelleKey, WellenZahlen & WellenQuoten>;
}

// ─── Nachfass ─────────────────────────────────────────────────────────────────────────────────

export interface NachfassZahlen {
  anmelder: number;
  im_segment: number;
  abdeckung: number | null;
  anmelder_mit_nachfass: number;
  im_segment_mit_nachfass: number;
  abdeckung_mit_nachfass: number | null;
  empfaenger: number;
  anfos: number;
  anfos_vorlaeufig: number;
  anfos_je_1000: number | null;
  wenig_daten: boolean;
  /** Erwarteter Wert (netto) wie die Nachfass-Zeile von P3 „Direkte Bestell-Links" (Persona-Umbau A1). */
  wert_summe: number;
  mit_wert: number;
  wert_je_1000: number | null;
  wert_je_anfo: number | null;
}

export interface NachfassHeatUp {
  kontakte: number;
  wert_summe: number;
  wert_je_1000: number | null;
  flaechen: { key: string; label: string; kontakte: number | null; wert_je_1000: number | null }[];
}

export interface NachfassZeile extends NachfassZahlen {
  publisher: string;
  label: string;
  bekannt: boolean;
}

export interface NachfassAntwort {
  window: { asOf: string; monate: KanalFensterMonate; von: string; bis: string; versand_bis: string };
  daten_ab: string;
  webinare_ab: string;
  reife_versand_tage: number;
  grau_schwellen: HebelGrauSchwellen;
  webinare: {
    im_fenster: number;
    mit_nachfass: number;
    abdeckung: number | null;
    ohne_nachfass: { webinar_id: string; public_title: string | null; start_date: string }[];
  };
  recipient_count: number;
  segmente: number;
  zeilen: NachfassZeile[];
  summe: NachfassZahlen;
  /** Heat-Up-Zeilen von P3 „Webinar-Einladungen" im selben Fenster (Vortag + Webinartag). */
  heatup: NachfassHeatUp | null;
}

// ─── Cross-Sell ───────────────────────────────────────────────────────────────────────────────

export interface CrossSellZeile {
  stufe: '1' | '2' | '3+' | 'gesamt';
  label: string;
  kunden: number;
  anteil: number | null;
  bestellungen: number;
  zahler: number;
  umsatz: number;
  umsatz_anteil: number | null;
  umsatz_je_kunde: number | null;
  tage_seit_erster: number | null;
  mehrprodukt: number;
}

export interface CrossSellAntwort {
  as_of: string;
  stufen: CrossSellZeile[];
  summe: CrossSellZeile;
  mehrprodukt_anteil: number | null;
  ohne_email: number;
  nicht_gezaehlt: { doppel: number; upsell_nachbar: number };
  vorlaeufig: number;
  sitzung_stunden: number;
}

const H = '/api/auswertungen/hebel';
export const fetchWellen = () => unwrap<WellenAntwort>(`${H}/wellen`);
export const fetchNachfass = (m: KanalFensterMonate) => unwrap<NachfassAntwort>(`${H}/nachfass?monate=${m}`);
export const fetchCrossSell = () => unwrap<CrossSellAntwort>(`${H}/cross-sell`);
